<template>
  <template v-if="card">
    <Wrap>
      <PhotosPreview
        :photos="card.photos"
        :experience="card.galleryExperience"
        :experience-id="card.galleryExperience.id"
        class="photos-preview"
      />
    </Wrap>
    <Header :crumbs="card.breadcrumbs" />
    <Wrap>
      <div class="card-content">
        <!-- h1 задаёт SEO, название достопримечательности остаётся запасным вариантом -->
        <SeoContent
          :heading="seo?.h1 || card.name"
          :description="card.description"
          :info="card.info"
          :sight-title="card.name"
          :sight-address="card.info.address ?? ''"
        />
        <Divider />
        <ExcursionSchedule :items="card.schedule" />
        <Divider v-if="!isDesktopSmall" />
        <ChooseExperienceBanner
          v-if="isDesktopSmall"
          class="card-content__banner"
          :count="card.experiences.count"
          :href="card.experiences.url"
        />
        <ChooseExperienceBannerMobile
          v-else
          class="card-content__banner-mobile"
          :count="card.experiences.count"
          :href="card.experiences.url"
        />
        <SightLocation v-if="card.location" class="card-content__location" :location="card.location" />
      </div>
    </Wrap>
    <article>
      <Wrap>
        <div class="post-wrap">
          <Description :data="description" :article-title="card.name" :article-url="canonicalUrl" />
        </div>
      </Wrap>
      <Wrap>
        <ExperienceListingStandalone
          :experiences="experiencesWithFavorite"
          analytic-events-prefix="sight"
          :show-location="true"
          :more-button="showMoreButton"
          :more-button-loading="isLoadingExperiences"
          :more-button-text="t('experiences.showAll')"
          :skeletons-count="6"
          @load-more="handleLoadMore"
          @card-click="handleCardClick"
          @set-favorite="handleSetFavorite"
          @unset-favorite="handleUnsetFavorite"
        />
      </Wrap>
      <Attractions
        :attractions="card.nearby.items"
        :title="nearbyTitle"
        :sights-url="card.nearby.url"
        @select="onAttractionSelect"
      />

      <Wrap>
        <FaqSection v-if="card.faq.items.length" :title="card.faq.title" :items="card.faq.items" />
        <SubscriptionBlock api-url="" :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL" />
      </Wrap>
    </article>
  </template>
  <AttractionPageSkeleton v-else-if="isLoading" />
  <NotFoundBlock v-else-if="hasError" api-url="" :sogu-url="VITE_RUNTIME_SOGU_SERVICE_URL" with-subscription-block />
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { NotFoundBlock } from '@experience/site-404-package';
import { SubscriptionBlock } from '@experience/subscription-block-package';
import { useHead, useSeoMeta } from '@unhead/vue';
import { computed, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';

import { Attractions, type NearbyAttraction } from '@/entities/attraction';
import { useExperienceEntity } from '@/entities/experience/entity';
import { ExperienceListingStandalone } from '@/features/experienceListing';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { setSightId, useAnalytics } from '@/shared/lib/analytics';
import { useRenderReport } from '@/shared/lib/lifecycle';
import Divider from '@/shared/ui/Divider.vue';
import Wrap from '@/shared/ui/Wrap.vue';
import { useScreenSize } from '@/shared/utils/breakpoints';
import { FaqSection } from '@/widgets/faqSection';
import { PhotosPreview } from '@/widgets/photosPreview';

import AttractionPageSkeleton from './components/AttractionPageSkeleton.vue';
import ChooseExperienceBanner from './components/ChooseExperienceBanner.vue';
import ChooseExperienceBannerMobile from './components/ChooseExperienceBannerMobile.vue';
import Description from './components/Description/Description.vue';
import ExcursionSchedule from './components/ExcursionSchedule.vue';
import Header from './components/Header.vue';
import SeoContent from './components/SeoContent.vue';
import SightLocation from './components/SightLocation.vue';
import { useArticleExperiences } from './composables/useArticleExperiences';
import { useArticleHashScroll } from './composables/useArticleHashScroll';
import { useAttractionPageData } from './composables/useAttractionPageData';
import ru from './i18n/ru.json';

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { isDesktopSmall } = useScreenSize();
useRenderReport();

const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();
const { trackEvent, trackEventImmediately } = useAnalytics();
const route = useRoute();
const experienceEntity = useExperienceEntity();

const { data, getAttraction, card, isLoading, hasError, seo, canonicalUrl, description } = useAttractionPageData(route);

useArticleHashScroll(
  route,
  computed(() => card.value?.id),
);

const {
  experiencesWithFavorite,
  isLoadingExperiences,
  showMoreButton,
  handleLoadMore,
  handleSetFavorite,
  handleUnsetFavorite,
  handleCardClick,
} = useArticleExperiences(data);

const coverPhotoUrl = computed(() => card.value?.photos[0]?.medium);

const nearbyTitle = computed(() => {
  const cityPhrase = card.value?.nearby.cityPhrase;

  return cityPhrase ? t('nearby.titleInCity', { city: cityPhrase }) : t('nearby.title');
});

const onAttractionSelect = async (attraction: NearbyAttraction) => {
  const url = `/sights/${attraction.id}-${attraction.slug}/`;

  try {
    await trackEventImmediately('sight:what-else:sight.click', {
      sight_id_clicked: attraction.id,
    });
  } catch {
    // переход не зависит от ответа аналитики
  }

  window.location.assign(url);
};

const pageUrl = computed(() => seo.value?.canonical_url || window.location.href);
// для достопримечательностей BFF присылает metatags без title/description, берём из карточки
const pageTitle = computed(() => seo.value?.title || seo.value?.h1 || card.value?.name);
const pageDescription = computed(() => seo.value?.description || card.value?.description);

useHead({
  title: () => pageTitle.value,
  link: [
    {
      rel: 'canonical',
      href: () => pageUrl.value,
    },
  ],
});

useSeoMeta({
  description: () => pageDescription.value,
  robots: () => (seo.value?.noindex ? 'noindex, nofollow' : 'index, follow'),
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  ogType: 'article',
  ogUrl: () => pageUrl.value,
  ogImage: () => coverPhotoUrl.value,
  ogImageWidth: 1220,
  ogImageHeight: 600,
  twitterCard: 'summary_large_image',
  twitterImage: () => coverPhotoUrl.value,
});

onBeforeMount(async () => {
  await getAttraction();
  void experienceEntity.loadFavorites();

  if (!card.value) {
    return;
  }

  setSightId(card.value.id);
  await trackEvent('sight.pageview', { experiences_count: card.value.experiences.count });
});
</script>

<!--
  Разметку контента рендерят дочерние компоненты articleContent, их узлы не получают
  scope-атрибут страницы, поэтому эти стили подключены вне scoped. Утечки нет: всё живёт
  внутри shadow root веб-компонента.
-->
<style lang="scss">
@import '@/shared/styles/post_page';
</style>

<style lang="scss" scoped>
.photos-preview {
  margin-bottom: 12px;
}

// на десктопе баннер выносится во вторую колонку и идёт параллельно основному контенту
.card-content {
  display: grid;
  grid-template-columns: minmax(0, 805px) minmax(300px, 1fr);
  column-gap: 40px;

  > * {
    grid-column: 1;
  }

  @include tablet {
    grid-template-columns: minmax(0, 805px);
  }
}

// sticky у grid-элемента ограничен его grid-областью, поэтому растягиваем её на все строки
// контента: строки неявные, из-за чего `-1` тут не работает, а лишние пустые строки
// нулевой высоты на раскладку не влияют — row-gap у грида нет
.card-content__banner {
  position: sticky;
  top: var(--alias-space-24, 24px);
  grid-row: 1 / span 100;
  grid-column: 2;
  place-self: start end;

  @include tablet {
    display: none;
  }
}

// баннер в потоке нужен только там, где нет второй колонки
.card-content__banner-mobile {
  display: none;

  @include tablet {
    display: flex;
  }
}

// в grid-контейнере вертикальные отступы не схлопываются, поэтому на десктопе,
// где над блоком остаётся только разделитель, отступ уменьшен на его margin
.card-content__location {
  margin-top: 56px;

  @include tablet {
    margin-top: 48px;
  }
}
</style>
